/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { IEpNavigationDrawerButton } from '../../interfaces/interfaces/components/IEpNavigationDrawer';

import {
  // IEpButtonGroupGeneral,
  IEpButtonGroupListData,
  IEpDropdownDefaultListItems,
} from '../../interfaces';
import { getDefaultAccess } from '../../utils/defaultAccess';
import {
  accountManagerRole,
  dashboardManageRole,
  networkManagerRole,
  notifierPharmacyRole,
  patientSafetyRole,
  pharmacyManagerRole,
  reportsRole,
} from '../../utils/roles';
import { EpButtonCircle } from '../EpButtonCircle';
import { EpButtonGroup } from '../EpButtonGroup';
import './EpProductNavigation.scss';
import { redirectToMonitor } from '../../utils/redirectToMonitor';

interface EpProductNavigationProps {
  productNavigationId: string;
  productNavigationButtonGroupId: string;
  productNavigationButtonCircleId?: string;
  productNavigationListData: any; //IEpHeaderNavigationMenuData | IEpButtonGroupGeneral[]
  productNavigationType?: 'primary' | 'secondary';
  productNavigationCustomPadding?: number;
  productNavigationDrawerId?: string;
  getButtonGroupWithDropdownData?: (
    dropdownData: IEpButtonGroupListData[],
  ) => void;
  customClasses?: string;
  onClickFunc?: (args?: unknown, arg?: 'primary' | 'secondary') => void;
}

const EpProductNavigation: React.FC<EpProductNavigationProps> = ({
  productNavigationId,
  productNavigationButtonGroupId,
  productNavigationButtonCircleId,
  productNavigationListData,
  productNavigationType = 'primary',
  productNavigationCustomPadding = 64,
  getButtonGroupWithDropdownData,
  customClasses = '',
  onClickFunc,
}) => {
  const [buttonGroupFormmatedData, setButtonGroupFormmatedData] = useState<
    IEpButtonGroupListData[]
  >([] as IEpButtonGroupListData[]);

  const roleId = getDefaultAccess()?.roleId.toLowerCase();
  const isValidAccessDashboardRoute = [
    patientSafetyRole,
    notifierPharmacyRole,
    accountManagerRole,
    networkManagerRole,
    reportsRole,
    pharmacyManagerRole,
    dashboardManageRole,
  ].includes(roleId);

  const formatButtonGroupData = useCallback(
    (buttonGroupArray: IEpNavigationDrawerButton[]) => {
      const buttonWithParent: IEpNavigationDrawerButton[] =
        buttonGroupArray.filter(
          ({ relativeParentId }: IEpNavigationDrawerButton) => relativeParentId,
        );
      const associationId: number | null | undefined =
        buttonWithParent[0]?.relativeParentId;
      const foundedParent: IEpNavigationDrawerButton | undefined =
        buttonGroupArray?.find(
          (button: IEpNavigationDrawerButton) => button.id === associationId,
        );

      const buttonGroupListData: IEpButtonGroupListData[] = [];

      const buttonWithoutParentOrNotIsParent: IEpNavigationDrawerButton[] =
        buttonGroupArray.filter(
          ({ relativeParentId, id }: IEpNavigationDrawerButton) =>
            !relativeParentId && id !== associationId,
        );

      if (buttonWithoutParentOrNotIsParent) {
        buttonWithoutParentOrNotIsParent.forEach(
          ({
            buttonId,
            buttonText,
            buttonOnClickFunc,
            buttonIsActive = false,
            buttonIsDisabled = false,
          }: IEpButtonGroupListData) => {
            buttonGroupListData.push({
              buttonId,
              buttonText,
              buttonOnClickFunc,
              buttonIsActive,
              buttonIsDisabled,
            });
          },
        );
      }

      if (buttonWithParent.length > 0) {
        const buttonWithParentFormmated: IEpDropdownDefaultListItems[] =
          buttonWithParent.map(
            ({
              buttonId,
              buttonText,
              buttonLinkHref,
              buttonLinkTarget = '_blank',
              buttonIsDisabled = false,
              buttonLeftIconName = '',
              buttonIsActive,
              buttonOnClickFunc,
            }: IEpButtonGroupListData) => ({
              buttonId,
              buttonText,
              buttonLinkHref,
              buttonLinkTarget,
              buttonIsActive,
              buttonIsDisabled,
              buttonLeftIconName,
              buttonOnClickFunc,
            }),
          );

        buttonGroupListData.push({
          buttonId: foundedParent?.buttonId ? foundedParent?.buttonId : '',
          buttonText: foundedParent?.buttonText,
          buttonIsDisabled: foundedParent?.buttonIsDisabled || false,
          buttonIsActive: foundedParent?.buttonIsActive,
          buttonLeftIconName: foundedParent?.buttonLeftIconName || '',
          dropdownEvent: 'click',
          dropdownId: `dropdown--${foundedParent?.id}`,
          buttonDropdownMenuListData: buttonWithParentFormmated,
        });
      }

      return buttonGroupListData;
    },
    [],
  );

  const handleButtonGroupFormmatedData = useCallback((): void => {
    if (productNavigationType === 'secondary') {
      const data = Array.isArray(productNavigationListData)
        ? productNavigationListData
        : [];
      return setButtonGroupFormmatedData(data as IEpButtonGroupListData[]);
    }

    if (!Array.isArray(productNavigationListData)) {
      return setButtonGroupFormmatedData([]);
    }

    setButtonGroupFormmatedData(
      productNavigationListData.flatMap((button: any) => {
        if (button.buttonGroupArray)
          return formatButtonGroupData(button.buttonGroupArray);
        return button;
      }),
    );
  }, [formatButtonGroupData, productNavigationListData, productNavigationType]);

  const setButtonInitiallyActive = (
    button: IEpButtonGroupListData,
    initiallyActiveItemId: string,
  ): IEpButtonGroupListData => {
    if (!button.buttonDropdownMenuListData) {
      button.buttonId === initiallyActiveItemId
        ? (button.buttonIsActive = true)
        : (button.buttonIsActive = false);
    } else {
      const foundedChildActive = button.buttonDropdownMenuListData.find(
        (dropdownBtn) => dropdownBtn.buttonId === initiallyActiveItemId,
      );

      if (foundedChildActive) {
        button.buttonIsActive = true;
        foundedChildActive.buttonIsActive = true;
      } else button.buttonIsActive = false;
    }

    return button;
  };

  const handleButtonInitiallyActive = (): void => {
    const { initiallyActiveItemId } = productNavigationListData as {
      initiallyActiveItemId: any;
    };

    if (!initiallyActiveItemId) return;

    setButtonGroupFormmatedData((prevButtons) =>
      prevButtons.map((button: IEpButtonGroupListData) =>
        setButtonInitiallyActive(button, initiallyActiveItemId),
      ),
    );
  };

  useEffect(() => {
    if (productNavigationType === 'secondary') return;
    handleButtonGroupFormmatedData();
    handleButtonInitiallyActive();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!getButtonGroupWithDropdownData) return;
    // getButtonGroupWithDropdownData(
    //   buttonGroupFormmatedData.filter(
    //     (button) => button.buttonDropdownMenuListData,
    //   ),
    // );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonGroupFormmatedData, productNavigationListData]);

  return (
    <div
      id={productNavigationId}
      className={`ep-product-navigation ep-product-navigation--${productNavigationType} ${customClasses}`}
      style={{ padding: `0 ${productNavigationCustomPadding}px` }}
      {...(onClickFunc && {
        onClick: (event) => onClickFunc(event, productNavigationType),
      })}
    >
      <div className="ep-product-navigation__wrapper--left">
        <EpButtonGroup
          buttonGroupId={'productNavigationButtonGroupId--jdhddgd'}
          buttonGroupType="link-nav-secondary"
          buttonGroupSize="sm"
          buttonGroupListData={
            Array.isArray(productNavigationListData)
              ? productNavigationListData
              : []
          }
          buttonGroupIsFullHeight
          customClasses="ep-product-navigation__button-group"
        />
      </div>

      {productNavigationType === 'primary' && isValidAccessDashboardRoute && (
        <div className="ep-product-navigation__wrapper--right">
          {/** @TODO - Verificar o código abaixo */}
          <EpButtonCircle
            buttonId={productNavigationButtonCircleId as string}
            buttonType="secondary"
            buttonSize="sm"
            buttonIconType="icon"
            buttonIconName="SolidChartBar"
            buttonLinkHref={redirectToMonitor({
              destinationUrl: process.env
                .NEXT_PUBLIC_DASHBOARD_MEDICAL_INTELLIGENCE as string,
            })}
          />
          {/* <button
            className="ep-product-navigation__button--hamburguer"
            data-bs-toggle="offcanvas"
            data-bs-target={`#${productNavigationDrawerId}`}
          >
            <EpIconSolidBars iconSize={20} />
          </button>

          <EpNavigationDrawer
            navigationDrawerId={productNavigationDrawerId as string}
            navigationDrawerButtonList={productNavigationListData?.items}
          /> */}
        </div>
      )}
    </div>
  );
};

export default EpProductNavigation;

/*
  Estado
  const [allEpButtons, setAllEpButtons] = useState<HTMLButtonElement[] | null>(null);

  Variáveis
  const allButtonsThatExceeded: HTMLButtonElement[] = [];
  let epProductNavigation: HTMLDivElement | null = null;
  let epButtonGroup: HTMLDivElement | null = null;
  let epButtonLeftWrapper: HTMLDivElement | null = null;

  Funções
  const initalizeVariables = (): void => {
    epProductNavigation = document.getElementById(productNavigationId) as HTMLDivElement;
    epButtonGroup = epProductNavigation.querySelector(`#${productNavigationButtonGroupId}`);
    epButtonLeftWrapper = epProductNavigation.querySelector('.ep-product-navigation__wrapper--left');

    console.log('epButtonLeftWrapper: ', epButtonGroup.querySelectorAll('.ep-button'));
    //setAllEpButtons(Array.from(epButtonGroup.querySelectorAll('.ep-button')));
  };

  const setButtonsThatExceeded = (button: HTMLButtonElement, parent: HTMLDivElement): void => {
    const currentBtnExeceedParent = button.getBoundingClientRect().right > parent.getBoundingClientRect().right;

    if (currentBtnExeceedParent)  allButtonsThatExceeded.push(button);

    else {
      const updatedButtonsThatExceeded = allButtonsThatExceeded.filter((btn) => btn !== button);
      setAllEpButtons(updatedButtonsThatExceeded);
      button.style.display = 'inline-block';
    }
  };

  const handleButtonThatExceeded = (): void => {
    allEpButtons.forEach((button) => setButtonsThatExceeded(button, epButtonLeftWrapper));
    allButtonsThatExceeded.forEach((button) => (button.style.display = 'none'));
  };

  const updateButtonsThatExceeded = (allButtons: HTMLButtonElement[]): void => {
    allButtons.forEach((button) => setButtonsThatExceeded(button, epButtonLeftWrapper));
    handleButtonThatExceeded();
  };
*/
